import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, Loader2, Tag, Check, Globe } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useCart } from "@/context/CartContext";
import { api, formatMoney } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fbTrack } from "@/lib/pixel";
import { toast } from "sonner";

const PAYPAL_CURRENCIES = ["AUD", "NZD", "USD", "GBP", "EUR", "CAD", "SGD", "JPY", "HKD"];

const PayPalCheckout = ({ currency, createOrder, onApprove }) => {
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();

  useEffect(() => {
    if (options.currency !== currency) {
      dispatch({ type: "resetOptions", value: { ...options, currency } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  if (isPending) {
    return <div className="flex justify-center py-3" data-testid="paypal-loading"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>;
  }
  return (
    <PayPalButtons
      style={{ layout: "horizontal", height: 44, tagline: false, color: "gold" }}
      forceReRender={[currency]}
      createOrder={createOrder}
      onApprove={onApprove}
      onCancel={() => toast.message("PayPal checkout cancelled — your cart is still here.")}
      onError={(err) => {
        const msg = String(err);
        if (!msg.includes("missing details") && !msg.includes("destroyed")) toast.error("PayPal checkout failed. Please try again or pay by card.");
      }}
    />
  );
};

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("AU");
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [paypal, setPaypal] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "", address: "", city: "", postcode: "" });

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/config/paypal").then((r) => setPaypal(r.data)).catch(() => {});
  }, []);

  const rule = countries.find((c) => c.code === country) || { rate: 0.1, label: "Tax", currency: "AUD", zone: "domestic" };
  const paypalOptions = useMemo(
    () => ({ "client-id": paypal?.client_id, currency: "AUD", intent: "capture" }),
    [paypal?.client_id]
  );
  const discount = promo ? subtotal * (promo.discount || 0) : 0;
  const taxable = subtotal - discount;
  const gst = taxable * rule.rate;
  const freeOver = rule.zone === "domestic" ? 75 : 250;
  const flat = rule.zone === "domestic" ? 9.99 : 24.99;
  const freeShip = promo?.free_shipping || taxable >= freeOver || subtotal === 0;
  const shipping = freeShip ? 0 : flat;
  const total = taxable + gst + shipping;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    try {
      const { data } = await api.get(`/promo/${code}`);
      setPromo(data);
      toast.success(`Promo applied: ${data.label}`);
    } catch {
      setPromo(null);
      toast.error("Invalid promo code");
    }
  };

  const validCustomer = () => {
    if (!customer.name || !customer.email || !customer.address) {
      toast.error("Please fill in your name, email and address.");
      return false;
    }
    return true;
  };

  const itemsPayload = () =>
    items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, size: i.size || null, design: i.design || null, personalization: i.personalization || null }));

  const checkout = async () => {
    if (!validCustomer()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: itemsPayload(),
        country,
        customer,
        promo_code: promo?.code || null,
        origin_url: window.location.origin,
      });
      localStorage.setItem("gobig_last_order", data.order_id);
      fbTrack("InitiateCheckout", {
        num_items: items.reduce((n, i) => n + i.quantity, 0),
        value: subtotal,
        currency: "NZD",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-28 text-center">
        <ShoppingBag size={48} className="mx-auto text-muted-foreground mb-6" />
        <h1 className="font-display text-3xl font-bold mb-3">Your cart is empty</h1>
        <p className="text-muted-foreground mb-8">Find your next best-seller.</p>
        <Link to="/shop" className="btn-primary" data-testid="cart-empty-shop-btn">Start shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <h1 className="font-display text-4xl font-black tracking-tighter mb-8">Your cart</h1>
        <div className="divide-y divide-ink/10 border-y border-ink/10">
          {items.map((i) => (
            <div key={`${i.product.id}-${i.size || ""}-${i.design || ""}-${i.personalization || ""}`} className="flex gap-4 py-5" data-testid={`cart-item-${i.product.id}`}>
              <img src={i.product.image} alt={i.product.name} className="w-24 h-24 object-cover border border-ink/10" />
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{i.product.category}</p>
                <h3 className="font-display font-bold">{i.product.name}</h3>
                {i.size && <p className="text-xs mt-0.5">Size: <span className="font-bold">{i.size}</span></p>}
                {i.design && <p className="text-xs mt-0.5">Design: <span className="font-bold">{i.design}</span></p>}
                {i.personalization && <p className="text-xs mt-0.5">Print name: <span className="font-bold">{i.personalization}</span></p>}
                <p className="font-mono font-bold mt-1">{formatMoney(i.product.price, rule.currency)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(i)} data-testid={`cart-remove-${i.product.id}`} className="text-muted-foreground hover:text-brand">
                  <Trash2 size={18} />
                </button>
                <div className="flex items-center border border-ink">
                  <button onClick={() => updateQuantity(i, i.quantity - 1)} className="px-2 py-1 hover:bg-secondary" data-testid={`cart-dec-${i.product.id}`}><Minus size={14} /></button>
                  <span className="px-3 text-sm font-bold">{i.quantity}</span>
                  <button onClick={() => updateQuantity(i, i.quantity + 1)} className="px-2 py-1 hover:bg-secondary" data-testid={`cart-inc-${i.product.id}`}><Plus size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={clearCart} className="text-sm text-muted-foreground hover:text-brand mt-4" data-testid="cart-clear-btn">Clear cart</button>
      </div>

      {/* Summary */}
      <div className="lg:col-span-1">
        <div className="border border-ink p-6 sticky top-20">
          <h2 className="font-display text-xl font-bold mb-5">Order summary</h2>

          <div className="mb-5">
            <label className="overline flex items-center gap-1 mb-2"><Globe size={12} /> Ship to</label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="country-select" className="w-full rounded-none border-ink"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`country-opt-${c.code}`}>
                    {c.name} · {c.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 mb-4 text-sm">
            {[["name", "Full name", "text"], ["email", "Email", "email"], ["address", "Street address", "text"]].map(([k, label, type]) => (
              <input key={k} type={type} placeholder={label} value={customer[k]} onChange={(e) => setCustomer({ ...customer, [k]: e.target.value })} data-testid={`checkout-${k}`} className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-2" />
            ))}
            <div className="flex gap-3">
              <input placeholder="City" value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} data-testid="checkout-city" className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-2" />
              <input placeholder="Postcode" value={customer.postcode} onChange={(e) => setCustomer({ ...customer, postcode: e.target.value })} data-testid="checkout-postcode" className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-2" />
            </div>
          </div>

          {/* Promo */}
          <div className="flex gap-2 mb-4">
            <div className="flex items-center flex-1 border border-ink/20 focus-within:border-brand px-2">
              <Tag size={14} className="text-muted-foreground" />
              <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="promo-input" className="w-full bg-transparent outline-none py-2 px-2 text-sm uppercase" />
            </div>
            <button onClick={applyPromo} data-testid="promo-apply" className="bg-ink text-white px-4 text-sm font-bold hover:bg-brand transition-colors">Apply</button>
          </div>
          {promo && <p className="text-green-600 text-xs flex items-center gap-1 mb-3" data-testid="promo-applied"><Check size={13} /> {promo.label} applied</p>}

          <div className="border-t border-ink/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">{formatMoney(subtotal, rule.currency)}</span></div>
            {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span data-testid="summary-discount">−{formatMoney(discount, rule.currency)}</span></div>}
            <div className="flex justify-between"><span>{rule.label}</span><span data-testid="summary-gst">{formatMoney(gst, rule.currency)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span data-testid="summary-shipping">{shipping === 0 ? "Free" : formatMoney(shipping, rule.currency)}</span></div>
            <div className="flex justify-between font-display text-lg font-bold pt-2 border-t border-ink/10"><span>Total</span><span data-testid="summary-total">{formatMoney(total, rule.currency)}</span></div>
          </div>

          <button onClick={checkout} disabled={loading} className="btn-primary w-full mt-6" data-testid="checkout-btn">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Redirecting…</> : <>Checkout securely</>}
          </button>

          {paypal?.client_id && (
            <PayPalScriptProvider options={paypalOptions}>
              {PAYPAL_CURRENCIES.includes(rule.currency.toUpperCase()) && (
                <div className="mt-4" data-testid="paypal-section">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-ink/10" />
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or pay with</span>
                    <div className="flex-1 h-px bg-ink/10" />
                  </div>
                  {paypal.mode === "sandbox" && (
                    <p className="text-[11px] text-amber-600 text-center mb-2" data-testid="paypal-sandbox-note">
                      PayPal test mode — no real money moves yet.
                    </p>
                  )}
                  <PayPalCheckout
                    currency={rule.currency.toUpperCase()}
                    createOrder={async () => {
                      if (!validCustomer()) throw new Error("missing details");
                      const { data } = await api.post("/paypal/create-order", {
                        items: itemsPayload(),
                        country,
                        customer,
                        promo_code: promo?.code || null,
                        origin_url: window.location.origin,
                      });
                      localStorage.setItem("gobig_last_order", data.order_id);
                      fbTrack("InitiateCheckout", { value: total, currency: rule.currency.toUpperCase() });
                      return data.paypal_order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await api.post(`/paypal/capture/${data.orderID}`);
                        if (res.data.status === "paid") {
                          navigate("/order/success?paypal=1", { state: { order: res.data.order } });
                        } else {
                          toast.error(res.data.detail || "PayPal did not complete the payment.");
                        }
                      } catch {
                        toast.error("PayPal payment could not be confirmed. Please contact support if you were charged.");
                      }
                    }}
                  />
                </div>
              )}
            </PayPalScriptProvider>
          )}

          <p className="text-[11px] text-muted-foreground text-center mt-3">Secure checkout via Stripe{paypal?.client_id ? " or PayPal" : ""} · {rule.currency} · Worldwide shipping</p>
          {items.reduce((s, i) => s + i.quantity, 0) > 1 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-center mt-3" data-testid="parcels-notice">
              📦 Your items may arrive in separate parcels — each ships as soon as it's ready.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
